<!--
 * @Description: 营销管理/活动运营报表/VIP会员卡报表
-->
<template>
  <scroll-layout>
    <template #header>
      <ykc-search
        ref="YkcSearch"
        :data="searchItems"
        :searchForm="searchParams"
        @click="handleSearchConfirm"
        @clean="handleSearchClean" />
    </template>
    <div>
      <!-- 汇总数据区 -->
      <div class="summary-cards">
        <div class="summary-card">
          <div class="summary-card__inner summary-card__inner--blue">
            <div class="summary-card__icon">
              <i class="el-icon-user-solid"></i>
            </div>
            <div class="summary-card__info">
              <p class="summary-card__label">拥有VIP会员卡用户数</p>
              <p class="summary-card__value">{{ totalUsers || 0 | numberFormat }}</p>
            </div>
          </div>
          <div class="summary-card__tip">
            <i class="el-icon-info"></i>
            数据统计时间范围：{{ queryTimeRange }}
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-card__inner summary-card__inner--green">
            <div class="summary-card__icon">
              <i class="el-icon-calendar"></i>
            </div>
            <div class="summary-card__info">
              <p class="summary-card__label">VIP会员卡总生效人天数</p>
              <p class="summary-card__value">{{ totalValidDays || 0 | numberFormat }}</p>
            </div>
          </div>
        </div>
      </div>

      <ykc-table
        ref="YkcTable"
        :data="tableData"
        :title="tableTitle"
        operateFixedType="right"
        :columns="tableColumns"
        :operateButtons="tableOperateButtons.filter(item => item.enabled())">
        <template #headerButtons>
          <div class="table-header-button">
            <ykc-button
              type="plain"
              v-rbac="'marketing:activityReport:vipCard:export'"
              @click="exportBlob">
              导出
            </ykc-button>
          </div>
        </template>
        <ykc-pagination
          slot="pagination"
          ref="YkcPagination"
          :total="total"
          :current-page.sync="currentPage"
          @current-change="handlePaginationCurrentChange"
          :page-size.sync="pageSize"
          @size-change="handlePaginationSizeChange" />
      </ykc-table>
    </div>
  </scroll-layout>
</template>

<script>
  import { offlineExport, getLastTime } from '@/utils';
  import { vipCardReport } from '@/service/apis';

  export default {
    name: 'vipCardReportList',
    data() {
      const startDate = getLastTime('yyyy-MM-dd', 30);
      const endDate = getLastTime('yyyy-MM-dd', 0);

      return {
        totalUsers: 0,
        totalValidDays: 0,
        queryTimeRange: '',
        searchParams: {
          queryTime: [startDate, endDate],
          queryTimeStart: startDate,
          queryTimeEnd: endDate,
        },
        tableData: [],
        total: 0,
        currentPage: 1,
        pageSize: 10,
        termList: [
          {
            title: 'VIP会员卡编码',
            desc: '系统自动生成的唯一标识码，用于识别每一张VIP会员卡。',
          },
          {
            title: 'VIP会员卡类型',
            desc: '包括月卡、季卡、年卡等不同时长的会员卡类型。',
          },
          {
            title: 'VIP会员卡状态',
            desc: '包括生效中、已过期、已取消等状态。',
          },
          {
            title: 'VIP会员卡生效时间',
            desc: '会员卡开始生效的日期时间。',
          },
          {
            title: 'VIP会员卡失效时间',
            desc: '会员卡到期的日期时间。',
          },
          {
            title: '购卡用户名称',
            desc: '购买该VIP会员卡的用户姓名或昵称。',
          },
          {
            title: 'VIP会员卡生效天数',
            desc: '会员卡实际生效的总天数。',
          },
        ],
        termMap: {
          validDays:
            '特定会员卡，特定时间段（查询时间段）内的生效天数；（注：取特定时间段（查询时间段）与会员卡生效时间段的重合天数）',
        },
        tableTitle: 'VIP会员卡数据列表',
        tableOperateButtons: [],
        tableColumns: [
          {
            label: 'VIP会员卡编码',
            prop: 'vipCode',
            minWidth: '180px',
          },
          {
            label: 'VIP会员卡类型',
            prop: 'vipType',
            minWidth: '140px',
          },
          {
            label: 'VIP会员卡状态',
            prop: 'vipStatus',
            minWidth: '120px',
          },
          {
            label: 'VIP会员卡生效时间',
            prop: 'effectTime',
            minWidth: '160px',
          },
          {
            label: 'VIP会员卡失效时间',
            prop: 'expireTime',
            minWidth: '160px',
          },
          {
            label: '购卡用户名称',
            prop: 'userName',
            minWidth: '140px',
          },
          {
            label: 'VIP会员卡生效天数',
            prop: 'validDays',
            minWidth: '160px',
            align: 'right',
            scopedSlots: {
              header: ({ column }) => this.renderHeaderWithTip(column.label, 'validDays'),
              default: ({ row }) => {
                return row.validDays != null ? `${Number(row.validDays).toLocaleString()}天` : '——';
              },
            },
          },
        ],
      };
    },
    computed: {
      searchItems() {
        return [
          {
            comName: 'YkcInput',
            key: 'vipCode',
            label: 'VIP会员卡编码',
            placeholder: '请输入VIP会员卡编码',
          },
          {
            comName: 'YkcInput',
            key: 'userName',
            label: '购卡用户名称',
            placeholder: '请输入购卡用户名称',
          },
          {
            comName: 'YkcDatePicker',
            type: 'daterange',
            format: 'yyyy-MM-dd',
            key: 'queryTime',
            label: '数据查询时间',
            limitDate: 'limitAfter',
          },
        ];
      },
    },
    created() {
      this.getData();
    },
    methods: {
      renderHeaderWithTip(label, prop) {
        const tip = this.termMap[prop];
        if (!tip) return label;
        return (
          <span style="display: inline-flex; align-items: center; gap: 4px;">
            {label}
            <el-tooltip content={tip} placement="top">
              <i
                class="el-icon-question"
                style="color: #909399; cursor: pointer; font-size: 14px;"
              />
            </el-tooltip>
          </span>
        );
      },
      /**
       * 点击查询
       */
      handleSearchConfirm(form) {
        this.currentPage = 1;
        const search = JSON.parse(JSON.stringify(form));
        if (search.queryTime && search.queryTime.length === 2) {
          [search.queryTimeStart, search.queryTimeEnd] = search.queryTime;
        }
        this.searchParams = search || {};
        this.getData();
      },
      /**
       * 点击清空查询按钮
       */
      handleSearchClean(form) {
        Object.assign(this.searchParams, form);
        const startDate = getLastTime('yyyy-MM-dd', 30);
        const endDate = getLastTime('yyyy-MM-dd', 0);
        this.searchParams.queryTime = [startDate, endDate];
        this.searchParams.queryTimeStart = startDate;
        this.searchParams.queryTimeEnd = endDate;
      },
      /**
       * 导出
       */
      exportBlob() {
        offlineExport({
          downloadType: 'marketing_export',
          jsonNode: {
            downloadKey: 'activity_report_vipCard',
            ...this.searchParams,
          },
        });
      },

      /**
       * 分页 change 事件监听
       */
      handlePaginationCurrentChange(current) {
        this.currentPage = current;
        this.getData();
      },
      /**
       * 分页 size change 事件监听
       */
      handlePaginationSizeChange(size) {
        this.currentPage = 1;
        this.pageSize = size;
        this.getData();
      },
      /**
       * 获取远程数据
       */
      getData() {
        const loading = this.$loading({
          lock: true,
          text: 'Loading',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)',
        });
        const listReq = vipCardReport.list({
          ...(this.searchParams || {}),
          current: this.currentPage,
          size: this.pageSize,
        });
        const summaryReq = vipCardReport.summary({
          ...(this.searchParams || {}),
        });
        Promise.all([listReq, summaryReq])
          .then(([listRes, summaryRes]) => {
            this.tableData = listRes.records || [];
            this.total = listRes.total || 0;
            this.totalUsers = summaryRes.totalParticipants || 0;
            this.totalValidDays = summaryRes.totalValidDays || 0;
            this.queryTimeRange = summaryRes.queryTimeRange || '';
          })
          .catch(err => {
            console.error('vipCardReport getData error:', err);
          })
          .finally(() => {
            loading.close();
          });
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/components/ykc-ui/styles/var.scss';

  .summary-cards {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
  }

  .summary-card {
    flex: 1;
    background: #fff;
    border: 1px solid #ebebf0;
    border-radius: 8px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &__inner {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 16px 20px;
      border-radius: 8px;
      border: 1px solid;

      &--blue {
        background: linear-gradient(to right, #eff6ff, #fff);
        border-color: #bfdbfe;
      }

      &--green {
        background: linear-gradient(to right, #ecfdf5, #fff);
        border-color: #a7f3d0;
      }
    }

    &__icon {
      width: 48px;
      height: 48px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;

      .summary-card__inner--blue & {
        background: rgba(37, 99, 235, 0.1);

        i {
          font-size: 24px;
          color: #2563eb;
        }
      }

      .summary-card__inner--green & {
        background: rgba(16, 185, 129, 0.1);

        i {
          font-size: 24px;
          color: #10b981;
        }
      }
    }

    &__info {
      display: flex;
      flex-direction: column;
    }

    &__label {
      font-size: 12px;
      color: #64748b;
      margin: 0 0 4px 0;
    }

    &__value {
      font-size: 24px;
      font-weight: 700;
      color: #1e293b;
      margin: 0;
    }

    &__tip {
      font-size: 12px;
      color: #64748b;
      margin-top: 8px;

      i {
        margin-right: 4px;
      }
    }
  }

  .term-list {
    .term-item {
      background: #f6f7f8;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }

      &__title {
        font-size: 14px;
        font-weight: 500;
        color: #1e293b;
        margin: 0 0 4px 0;
      }

      &__desc {
        font-size: 13px;
        color: #64748b;
        margin: 0;
      }
    }
  }
</style>
